/**
 * Pure position-sizing math. UI-free so it can be tested + reused.
 */

export type RiskInput = {
  accountSizeUsd: number;
  riskPct: number; // % of account willing to lose on this trade (0.1 to 5)
  entryPrice: number;
  stopPrice: number;
};

export type RiskOutput = {
  riskUsd: number;
  stopDistance: number;
  positionUnits: number;
  notionalUsd: number;
  warnings: string[];
};

const HARD_RISK_CAP_PCT = 5;
const SOFT_RISK_CAP_PCT = 2;

export function calcRisk(input: RiskInput): RiskOutput {
  const { accountSizeUsd, riskPct, entryPrice, stopPrice } = input;
  const warnings: string[] = [];

  if (riskPct > HARD_RISK_CAP_PCT) {
    warnings.push(`Risking >${HARD_RISK_CAP_PCT}% per trade is account-suicide territory.`);
  } else if (riskPct > SOFT_RISK_CAP_PCT) {
    warnings.push(`Risking >${SOFT_RISK_CAP_PCT}% per trade is aggressive; most prop firms reward consistency.`);
  }

  const riskUsd = (accountSizeUsd * riskPct) / 100;
  const stopDistance = Math.abs(entryPrice - stopPrice);

  if (stopDistance === 0) {
    return { riskUsd, stopDistance: 0, positionUnits: 0, notionalUsd: 0, warnings: [...warnings, "Entry and stop are identical."] };
  }

  const positionUnits = riskUsd / stopDistance;
  const notionalUsd = positionUnits * entryPrice;

  // We deliberately do NOT warn on high notional/account ratios. Forex traders
  // running 1:100 or 1:200 leverage routinely hit 50x+ notional on legitimate
  // trades; warning on it produced false positives. Notional is surfaced as a
  // fact in the UI; leverage caps belong on the firm dossier, not here.

  return { riskUsd, stopDistance, positionUnits, notionalUsd, warnings };
}
